import { useEffect, useState } from 'react';
import { _notNil } from '@/littledash';
import type {
  DataTableCellAlertUpdateEvent,
  DataTableCellIdCoordinate,
  DataTableCellReference,
} from '../DataTable.model';
import { DataTableEvent } from '@/components/UI/DataTableV2/DataTable.model';
import { useDataTable } from '../DataTableProvider';

export interface UseCellAlertsOptions {
  enabled?: boolean;
}

export interface UseCellAlertsReturn {
  hasAlert: boolean;
  alertCount: number;
}

/**
 * Subscribe to cell alert updates
 *
 * This hook manages cell alert state and automatically updates when the
 * DataTableService broadcasts CellAlertUpdate events. Uses AbortController for cleanup.
 * Can be disabled for cells that don't display alerts (like TimestampCell).
 *
 * @param idCoordinate - Cell ID coordinate (API IDs)
 * @param cellRef - Cell reference string for event filtering
 * @param options - Optional configuration (enabled flag)
 * @returns Object containing alert state
 */
export const useCellAlerts = (
  idCoordinate: DataTableCellIdCoordinate | undefined,
  cellRef: DataTableCellReference,
  options: UseCellAlertsOptions = {}
): UseCellAlertsReturn => {
  const { enabled = true } = options;
  const { dataTableService } = useDataTable();
  const [hasAlert, setHasAlert] = useState(false);
  const [alertCount, setAlertCount] = useState(0);

  useEffect(() => {
    if (enabled && _notNil(idCoordinate)) {
      const abortController = new AbortController();

      // Initialize alert state
      const initialAlerts = dataTableService.cellAlerts(idCoordinate);
      const initialCount = initialAlerts?.alerts?.size ?? 0;
      setAlertCount(initialCount);
      setHasAlert(initialCount > 0);

      const cellAlertUpdateListener = (event: DataTableCellAlertUpdateEvent) => {
        if (Object.prototype.hasOwnProperty.call(event.detail.cellAlerts, cellRef)) {
          const count = event.detail.cellAlerts[cellRef]?.alerts?.size ?? 0;
          setAlertCount(count);
          setHasAlert(count > 0);
        }
      };

      dataTableService.subscribe(DataTableEvent.CellAlertUpdate, cellAlertUpdateListener, abortController.signal);

      return () => {
        abortController.abort();
      };
    }
  }, [dataTableService, idCoordinate, cellRef, enabled]);

  return { hasAlert, alertCount };
};
